import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import moment from "moment";
import styled from "@emotion/styled";
import { storeOperations, storeSelector } from "state/ducks/store";

const ExcelButton = props => {
  const { establishmentId, types, categoriesFilter } = props;

  const [loadExcel, setLoadExcel] = useState(false);
  // utile pour calculer les dates
  const [weekPrinted, setWeekPrinted] = useState(0);

  // Cherche les stocks et le détail de l'établissement
  const forecastStocks = useSelector(state => {
    return storeSelector.getForecastStocksPrintSelector(state);
  });
  const establishment = useSelector(state => state.session.establishment);

  const dispatch = useDispatch();

  /**
   * Lance la requete pour les forecast stock
   */
  const generateExcel = week => {
    setWeekPrinted(week);
    let categoriesTransform = [];
    if (categoriesFilter) {
      categoriesFilter.forEach((category, key) => {
        categoriesTransform = [...categoriesTransform, category.value];
      });
    }

    setLoadExcel(true);
    dispatch(
      storeOperations.getForecastStocks(
        establishmentId,
        types,
        categoriesTransform,
        [week],
        [1, 2, 3, 4, 5],
        0,
        null,
        true,
        true
      )
    );
  };

  /**
   * Génère le fichier Excel à partir des données
   */
  const createExcelFile = (stocksData, startDate, endDate) => {
    const workbook = XLSX.utils.book_new();
    
    // Mapping des types pour l'affichage
    const typeLabels = {
      "shop_v2": "Btq",
      "delivery": "Dlvro",
      "catering": "Char",
      "total": "Tot"
    };
    
    // Mapping des jours pour l'affichage
    const dayLabels = {
      "lundi": "Lundi",
      "mardi": "Mardi",
      "mercredi": "Mercredi",
      "jeudi": "Jeudi",
      "vendredi": "Vendredi"
    };

    stocksData.printData.forEach((category, catKey) => {
      // Récupérer la structure des jours et types depuis le premier produit
      let daysOrder = [];
      let typesOrder = [];
      
      // Trouver le premier produit valide avec des périodes
      if (category.products && Array.isArray(category.products) && category.products.length > 0) {
        const firstProductWithPeriods = category.products.find(
          product => product && product.periods && Array.isArray(product.periods) && product.periods.length > 0 && product.periods[0] && product.periods[0].days
        );
        
        if (firstProductWithPeriods && firstProductWithPeriods.periods[0].days) {
          daysOrder = Object.keys(firstProductWithPeriods.periods[0].days);
          if (daysOrder.length > 0 && firstProductWithPeriods.periods[0].days[daysOrder[0]] && firstProductWithPeriods.periods[0].days[daysOrder[0]].types) {
            typesOrder = Object.keys(firstProductWithPeriods.periods[0].days[daysOrder[0]].types);
          }
        }
      }

      // Créer les en-têtes pour chaque catégorie
      const headers = ["Produit", "Stock"];
      daysOrder.forEach(dayKey => {
        const dayLabel = dayLabels[dayKey] || dayKey;
        typesOrder.forEach(typeKey => {
          const typeLabel = typeLabels[typeKey] || typeKey;
          headers.push(`${dayLabel} - ${typeLabel}`);
        });
      });

      // Créer les lignes de données
      const rows = [headers];

      // Ligne de titre avec les dates
      const titleRow = [`Du ${startDate} au ${endDate}`, ""];
      daysOrder.forEach(() => {
        typesOrder.forEach(() => {
          titleRow.push("");
        });
      });
      rows.push(titleRow);

      // Ligne vide
      rows.push([]);

      // Ligne avec le nom de la catégorie
      const categoryRow = [category.name, ""];
      daysOrder.forEach(() => {
        typesOrder.forEach(() => {
          categoryRow.push("");
        });
      });
      rows.push(categoryRow);

      // Ligne vide
      rows.push([]);

      // Parcourir les produits
      let hasProducts = false;
      if (category.products && Array.isArray(category.products) && category.products.length > 0) {
        category.products.forEach((product, productKey) => {
          if (!product) return; // Ignorer les produits null/undefined
          
          const productRow = [product.name || "", ""];
          
          // Pour chaque jour
          daysOrder.forEach((dayKey) => {
            // Pour chaque type
            typesOrder.forEach((typeKey) => {
              if (product.periods && Array.isArray(product.periods) && product.periods.length > 0 && product.periods[0] && product.periods[0].days && product.periods[0].days[dayKey]) {
                const dayData = product.periods[0].days[dayKey];
                if (dayData.types && dayData.types[typeKey]) {
                  const typeData = dayData.types[typeKey];
                  let value = "";
                  
                  if (typeData.qty === -1) {
                    value = "∞";
                  } else if (typeData.active === false) {
                    value = "inactif";
                  } else {
                    value = typeData.qty;
                  }
                  
                  productRow.push(value);
                } else {
                  productRow.push("");
                }
              } else {
                productRow.push("");
              }
            });
          });
          
          rows.push(productRow);
          hasProducts = true;
        });
      }

      // Ne créer la feuille que s'il y a des produits
      if (hasProducts) {
        // Créer la feuille de calcul
        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        
        // Ajuster la largeur des colonnes
        const colWidths = [
          { wch: 30 }, // Produit
          { wch: 8 },  // Stock
        ];
        daysOrder.forEach(() => {
          typesOrder.forEach(() => {
            colWidths.push({ wch: 10 });
          });
        });
        worksheet["!cols"] = colWidths;

        // Appliquer le style rouge aux colonnes de totaux
        // Calculer les indices de colonnes pour les totaux
        const totalColumnIndices = [];
        let colIndex = 2; // Commence après "Produit" et "Stock"
        
        daysOrder.forEach((dayKey) => {
          typesOrder.forEach((typeKey) => {
            if (typeKey === "total") {
              totalColumnIndices.push(colIndex);
            }
            colIndex++;
          });
        });

        // Style rouge pour les totaux
        const redStyle = {
          font: { color: { rgb: "FF0000" } }
        };

        // Appliquer le style rouge à toutes les cellules des colonnes de totaux
        const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
        for (let row = 0; row <= range.e.r; row++) {
          totalColumnIndices.forEach(colIdx => {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIdx });
            if (!worksheet[cellAddress]) {
              worksheet[cellAddress] = { v: "", t: "s" };
            }
            // Appliquer le style rouge
            worksheet[cellAddress].s = redStyle;
          });
        }

        // Ajouter la feuille au workbook
        const sheetName = category.name.length > 31 ? category.name.substring(0, 31) : category.name;
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      }
    });

    // Générer le fichier
    const weeknumber = moment().week();
    const start = moment()
      .day(1)
      .week(weeknumber + weekPrinted);
    const end = moment()
      .day(7)
      .week(weeknumber + weekPrinted);
    const startFormattedFileName = moment(start).format("DD-MM-YYYY");
    const endFormattedFileName = moment(end).format("DD-MM-YYYY");
    const fileName =
      "besoin-produit-" +
      (establishment === 1 ? "L" : "V") +
      "-du-" +
      startFormattedFileName +
      "-au-" +
      endFormattedFileName +
      ".xlsx";

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, fileName);
  };

  useEffect(() => {
    if (
      loadExcel === true &&
      forecastStocks.printData &&
      forecastStocks.printData.length > 0 &&
      forecastStocks.isLoading === false
    ) {
      const weeknumber = moment().week();
      const start = moment()
        .day(1)
        .week(weeknumber + weekPrinted);
      const end = moment()
        .day(7)
        .week(weeknumber + weekPrinted);
      const startFormattedFileName = moment(start).format("DD-MM-YYYY");
      const endFormattedFileName = moment(end).format("DD-MM-YYYY");
      
      createExcelFile(forecastStocks, startFormattedFileName, endFormattedFileName);
      setLoadExcel(false);
    }
  }, [loadExcel, establishment, forecastStocks, weekPrinted]);

  return (
    <div>
      {!loadExcel ? (
        <>
          <Item onClick={() => generateExcel(-1)}>Semaine - 1</Item>
          <Item onClick={() => generateExcel(0)}>Semaine en cours</Item>
          <Item onClick={() => generateExcel(1)}>Semaine + 1</Item>
          <Item onClick={() => generateExcel(2)}>Semaine + 2</Item>
        </>
      ) : (
        "Géneration du fichier Excel en cours..."
      )}
    </div>
  );
};

const Item = styled(Dropdown.Item)`
  border-bottom: solid 1px #ccc;
  &:hover {
    background-color: #ddd;
  }
`;

ExcelButton.propTypes = {
  establishmentId: PropTypes.string, // Id de l'établissent
  types: PropTypes.array, // Liste des types d'activité pour les stocks
  categoriesFilter: PropTypes.array // La valeur du filtre catégorie
};

export default ExcelButton;
